import {
  type PromotionalBannerAddUiFlagMutation,
  type UserInterfaceFlag,
} from '#app/__generated__/relay/PromotionalBannerAddUiFlagMutation.graphql.js';
import {
  SIDEBAR_CLOSED_WIDTH,
  SIDEBAR_OPEN_WIDTH,
} from '#app/constants/sidenav.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useNavbar } from '#app/hooks/useNavbar.js';
import { PropertyLinkName } from '#app/types/analytics/propertyLibrary.js';
import { CloseIcon, IconSvgProps } from '@contra/icons/icons';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { ComponentType, useCallback } from 'react';
import { css, styled } from 'styled-components';

const StyledPromotionalBanner = styled.div<{
  backgroundGradient?: string[];
  isSidenavDocked: boolean;
  minHeight?: number;
}>`
  ${({
    backgroundGradient = ['#f3ca4d 0%', '#ff989a 42.88%', '#cbabff 94.62%'],
    isSidenavDocked,
    minHeight = 40,
    theme,
  }) => css`
    background: ${theme.colorsV2.gray05};
    min-height: ${minHeight}px;
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    padding: 8px 16px;
    background: linear-gradient(90deg, ${backgroundGradient.join(', ')});
    background: linear-gradient();
    font-size: 16px;
    z-index: ${theme.zIndices.navbar};
    line-height: 1.5;
    font-weight: 500;
    color: ${theme.colorsV2.black};
    text-align: center;

    ${theme.mediaQueries.lg} {
      margin-left: ${isSidenavDocked
        ? SIDEBAR_OPEN_WIDTH
        : SIDEBAR_CLOSED_WIDTH}px;
      width: calc(
        100% - ${isSidenavDocked ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH}px
      );
    }
  `}
`;

const TextLink = styled.a`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
    font-weight: 500;
    font-size: 22px;
    letter-spacing: -0.5px;
  `}
`;

const CloseButton = styled.button`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    margin-left: auto;
  `}
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const StyledCloseIcon = styled(CloseIcon)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
  `}
`;

export const PromotionalBanner = ({
  backgroundGradient,
  href,
  icon: Icon = undefined,
  isSidenavDocked,
  minHeight = 40,
  textContent,
  trackingLinkName,
  uiFlag = null,
}: {
  readonly backgroundGradient?: string[];
  readonly href: string;
  readonly icon?: ComponentType<IconSvgProps>;
  readonly isSidenavDocked: boolean;
  readonly minHeight?: number;
  readonly textContent: string;
  readonly trackingLinkName: PropertyLinkName;
  readonly uiFlag: null | UserInterfaceFlag;
}) => {
  const { track } = useAnalytics();

  const { showPromotionalBanner } = useNavbar({});

  const [addUIFlag] = useContraMutation<PromotionalBannerAddUiFlagMutation>(
    graphql`
      mutation PromotionalBannerAddUiFlagMutation(
        $input: AddUserInterfaceFlagInput!
      ) {
        addUserInterfaceFlag(input: $input) {
          errors {
            __typename
            message
          }
          userAccount {
            id
            userInterfaceFlags
          }
        }
      }
    `,
  );

  const handleClose = useCallback(() => {
    if (!uiFlag) return;

    addUIFlag({
      onCompleted: () => {},
      onError: (error) => {
        captureException(error);
      },
      variables: {
        input: {
          userInterfaceFlag: uiFlag,
        },
      },
    });
  }, [addUIFlag, uiFlag]);

  return showPromotionalBanner ? (
    <StyledPromotionalBanner
      backgroundGradient={backgroundGradient}
      isSidenavDocked={isSidenavDocked}
      minHeight={minHeight}
    >
      <TextContainer>
        <TextLink
          href={href}
          onClick={() => {
            track('link', {
              action: 'tapped',
              link_name: trackingLinkName,
              section: 'promotional_banner',
            });
          }}
          target="_blank"
        >
          {textContent}
        </TextLink>
        {Icon ? <Icon /> : null}
      </TextContainer>
      {uiFlag ? (
        <CloseButton
          aria-label="Close survey banner"
          onClick={handleClose}
        >
          <StyledCloseIcon />
        </CloseButton>
      ) : null}
    </StyledPromotionalBanner>
  ) : null;
};
